// src/components/Layout.jsx
import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import "./Layout.css";
import { useLanguage } from "../i18n/LanguageContext";
import logo512 from "../assets/landing/logo_512.png"; 
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";


const linkStyle = (active) => ({
  padding: "0.5rem 0.75rem",
  borderRadius: "0.5rem",
  textDecoration: "none",
  color: active ? "#111827" : "white",
  backgroundColor: active ? "white" : "transparent",
  fontWeight: active ? "600" : "400",
  fontSize: "0.95rem"
});

const logoutButtonStyle = {
  marginTop: "1.5rem",
  padding: "0.5rem 0.75rem",
  borderRadius: "0.5rem",
  border: "none",
  width: "100%",
  textAlign: "left",
  cursor: "pointer",
  backgroundColor: "#DC2626",
  color: "white",
  fontWeight: "600",
};

export default function Layout() {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [uid, setUid] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [songUpdates, setSongUpdates] = useState(0);


  // ✅ NUEVO: desplegable "Crear"
  const [isCreateOpen, setIsCreateOpen] = useState(() => {
    // si estás en alguna ruta de crear, que arranque abierto
    return (
      pathname.startsWith("/app/crear-") ||
      pathname.startsWith("/app/create")
    );
  });

  const { lang, setLang, t } = useLanguage();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const isActiveAppHome = pathname === "/app";
  const isActive = (subPath) => pathname.startsWith(`/app/${subPath}`);

  // ✅ helper para marcar activo dentro de "Crear"
  const isActiveCreate = (subPath) => pathname.startsWith(`/app/${subPath}`);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || null);
    });
    return () => unsubAuth();
  }, []);

 useEffect(() => {
  console.log("[Layout] uid:", uid);

  if (!uid) return;

  const ref = doc(db, "usuarios", uid, "notifs", "summary");

  const unsub = onSnapshot(
    ref,
    (snap) => {
      console.log("[Layout] notifs exists:", snap.exists());
      console.log("[Layout] notifs data:", snap.data());

      const d = snap.data() || {};
      setChatUnread(Number(d.chatUnread || 0));
      setSongUpdates(Number(d.songUpdates || 0));
    },
    (err) => {
      console.error("[Layout] onSnapshot error:", err);
    }
  );

  return () => unsub();
}, [uid]);



  return (
    <div className="app-layout">
      {/* Header solo móviles */}
      <header className="mobile-header">
        <div className="mobile-header-left">
          <span className="app-logo-text">{t("app.name")}</span>
        </div>

        <button
          type="button"
          className="mobile-menu-button"
          onClick={toggleMenu}
          aria-label={t("layout.mobile.menuAria")}
        >
          <span />
          <span />
          <span />
        </button>
      </header>



      {/* Overlay móvil */}
      {isMenuOpen && <div className="mobile-overlay" onClick={closeMenu} />}

      {/* Sidebar */}
      <aside className={`sidebar ${isMenuOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <img src={logo512} alt="Logo" className="sidebar-logo" />
          <h2 className="sidebar-title">{t("layout.sidebar.title")}</h2>
        </div>



       
        {/* ✅ Selector de idioma con mejor formato */}
        <div className="sidebar-lang">
          <div className="sidebar-lang-title">{t("common.language")}</div>

          <select
            id="lang-select"
            className="sidebar-lang-select"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
          >
            <option value="en">English</option> 
            <option value="es">Español</option>                       
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="pt">Português</option>
          </select>
        </div>

        <nav className="sidebar-nav">
          <Link style={linkStyle(isActiveAppHome)} to="" onClick={closeMenu}>
            {t("layout.sidebar.dashboard")}
          </Link>
        
          <Link
            className="navLinkWithBadge"
            style={linkStyle(isActive("songs"))}
            to="songs"
            onClick={closeMenu}
          >
            <span>{t("layout.sidebar.songs")}</span>
            {songUpdates > 0 ? <span className="dotBadge" aria-label="new" /> : null}
          </Link>


          <Link style={linkStyle(isActive("letras"))} to="letras" onClick={closeMenu}>
            {t("layout.sidebar.lyrics")}
          </Link>

          {/* ✅ NUEVO: Grupo "Crear" */}
          <div className="sidebar-group">
            <button
              type="button"
              className={`sidebar-group-btn ${isCreateOpen ? "open" : ""}`}
              onClick={() => setIsCreateOpen((v) => !v)}
              aria-expanded={isCreateOpen}
            >
              <span>{t("layout.sidebar.createGroup")}</span>
              <span className="chev">{isCreateOpen ? "▾" : "▸"}</span>
            </button>

            {isCreateOpen && (
              <div className="sidebar-group-items">
                <Link
                  className={`sidebar-subLink ${isActiveCreate("crear-cancion") ? "active" : ""}`}
                  to="crear-cancion"
                  onClick={closeMenu}
                >
                  {t("layout.sidebar.createSongIdeas")}
                </Link>

                <Link
                  className={`sidebar-subLink ${isActiveCreate("crear-letra") ? "active" : ""}`}
                  to="crear-letra"
                  onClick={closeMenu}
                >
                  {t("layout.sidebar.createLyrics")}
                </Link>

                {/* OJO: aquí tenías un typo: "crear-can-con-letra" vs "crear-cancion-con-letra"
                    Usa la ruta que tengas realmente en App.js */}
                <Link
                  className={`sidebar-subLink ${isActiveCreate("crear-can-con-letra") ? "active" : ""}`}
                  to="crear-can-con-letra"
                  onClick={closeMenu}
                >
                  {t("layout.sidebar.createSongWithLyrics")}
                </Link>

                <Link
                  className={`sidebar-subLink ${isActiveCreate("create") ? "active" : ""}`}
                  to="create"
                  onClick={closeMenu}
                >
                  {t("layout.sidebar.wizard")}
                </Link>
              </div>
            )}

          </div>

          <Link style={linkStyle(isActive("perfil"))} to="perfil" onClick={closeMenu}>
            {t("layout.sidebar.profile")}
          </Link>

          <Link
            className="navLinkWithBadge"
            style={linkStyle(isActive("chat-support"))}
            to="chat-support"
            onClick={closeMenu}
          >
            <span>{t("chat.chat")}</span>
            {chatUnread > 0 ? <span className="dotBadge" aria-label="new" /> : null}
          </Link>

          <Link style={linkStyle(isActive("pagos"))} to="pagos" onClick={closeMenu}>
            {t("layout.payments")}
          </Link>
        </nav>

       
        <button
          type="button"
          style={logoutButtonStyle}
          onClick={() => {
            closeMenu();
            handleLogout();
          }}
        >
          {t("common.logout")}
        </button>
      </aside>

      {/* Contenido */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
